import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, RefreshCw, ExternalLink } from "lucide-react";
import api from "../../lib/api";

const SLOTS = [["blatt", "Blatt"], ["bluete", "Blüte"], ["frucht", "Frucht"], ["standort", "Standort"], ["detail", "Detail"], ["galerie", "Galerie"]];
const LABEL_TO_SLOT = Object.fromEntries(SLOTS.map(([v, l]) => [l, v]));

function PhotoTile({ ph, onDone }) {
  const [busy, setBusy] = useState("");
  const [slot, setSlot] = useState(LABEL_TO_SLOT[ph.slot] || "galerie");
  const changeSlot = async (v) => {
    setBusy("slot");
    try {
      await api.post("/admin/photo-review/slot", { plant_id: ph.plant_id, url: ph.url, slot: v });
      setSlot(v);
      onDone("slot");
      toast.success(`${ph.plant_name}: Bildart „${SLOTS.find((s) => s[0] === v)[1]}“ gespeichert`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setBusy("");
    }
  };
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "ok") {
        await api.post("/admin/photo-review/ok", { plant_id: ph.plant_id, url: ph.url });
      } else {
        const { data } = await api.post(`/admin/plants/${ph.plant_id}/reject-photo`, { url: ph.url });
        toast.success(data.replaced ? `${ph.plant_name}: Ersatzfoto geholt – erscheint unten zur Prüfung` : `${ph.plant_name}: Foto entfernt, kein Ersatz gefunden`);
      }
      onDone(kind);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="photo-check-tile">
      <img src={ph.url} alt={ph.plant_name} loading="lazy" className="w-full aspect-square object-cover" />
      <div className="p-2 space-y-1.5">
        <div className="text-xs leading-tight"><b>{ph.plant_name}</b> · {ph.slot === "Hauptbild" ? "Hauptbild" : (
          <select value={slot} disabled={!!busy} onChange={(e) => changeSlot(e.target.value)} className="text-xs border rounded px-1 py-0.5 bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="photo-check-slot">
            {SLOTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}</div>
        <div className="text-[10px] opacity-60 italic truncate" title={ph.source_title}>{ph.name_lat}</div>
        <div className="flex gap-1">
          <button onClick={() => run("ok")} disabled={!!busy} className="flex-1 inline-flex items-center justify-center gap-1 py-1 rounded-full border text-[11px] hover:bg-[var(--wp-cream)]" style={{ borderColor: "var(--wp-border)" }} data-testid="photo-check-ok">
            {busy === "ok" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Passt
          </button>
          <button onClick={() => run("reject")} disabled={!!busy} className="flex-1 inline-flex items-center justify-center gap-1 py-1 rounded-full border text-[11px] hover:bg-[var(--wp-cream)]" style={{ borderColor: "var(--wp-border)", color: "var(--wp-toxic)" }} data-testid="photo-check-reject">
            {busy === "reject" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Falsch
          </button>
          {ph.source_url && (
            <a href={ph.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2 rounded-full border" style={{ borderColor: "var(--wp-border)" }} title="Auf Wikimedia ansehen" data-testid="photo-check-source"><ExternalLink className="w-3 h-3" /></a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PhotoCheckList() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/photo-review").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows) return <div className="text-xs opacity-60"><Loader2 className="w-3.5 h-3.5 inline animate-spin" /> Lädt Fotos …</div>;
  return (
    <div className="space-y-2" data-testid="photo-check-list">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium" data-testid="photo-check-count">{rows.length} automatisch geholte Fotos ungeprüft</div>
        <button onClick={load} className="text-xs underline opacity-70" data-testid="photo-check-reload">Neu laden</button>
      </div>
      <p className="text-xs opacity-70">„Passt“ blendet das Foto aus der Liste aus. „Falsch“ löscht es und holt automatisch ein anderes von Wikimedia.</p>
      {rows.length === 0 && <div className="text-xs opacity-60">Alles geprüft.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {rows.map((ph) => (
          <PhotoTile key={ph.plant_id + ph.url + ph.slot} ph={ph} onDone={(kind) => (kind === "ok" ? setRows((r) => r.filter((x) => x !== ph)) : load())} />
        ))}
      </div>
    </div>
  );
}
